import React, {useEffect, useRef, useState} from 'react';
import './index.css';
import FloatingInput from "../FloatingInput";
import type { IRoof, IRoofAddress } from "../../react-app-env";
import { defaultAddress } from "../../utils/map";
import { isWholeNumber, isPositiveInteger } from "../../utils/util";
import {
	directionDegrees,
	maxArea,
	maxLengthWidth,
	maxSlope,
	minArea,
	minLengthWidth,
	minSlope
} from "../../config/config";
import OrientationPicker from "../OrientationPicker";

interface P {
	tabIndex: number
	i18nMap: any
	selectedDraft: IRoofAddress
	updateDraft: any
}

export default ({ tabIndex, i18nMap, selectedDraft, updateDraft, }: P) => {

	const slopeRef = useRef<any>(null);

	const defaultOrientation = {label: 'unselectedSlope', value: -1};

	const [flashingFields, setFlashingFields] = useState(
		{
			length: false,
			width: false,
			area: false,
		}
	);
	const handleFlashing = (newState: {
		length?: boolean,
		width?: boolean,
		area?: boolean,
	}) => {
		setFlashingFields(prevState => ({
			...prevState,
			...newState
		}));
		// Reset flashing effect
		setTimeout(() => setFlashingFields({
			length: false,
			width: false,
			area: false,
		}), 2000);
	}

	const [errorFields, setErrorFields] = useState(
		{
			length: false,
			width: false,
			area: false,
		}
	);
	const handleError = (newState: {
		length?: boolean,
		width?: boolean,
		area?: boolean,
	}) => {
		setErrorFields(prevState => ({
			...prevState,
			...newState
		}));
		// Reset error effect
		setTimeout(() => setErrorFields({
			length: false,
			width: false,
			area: false,
		}), 2000);
	}

	const [lastChanged, setLastChanged] = useState<"l" | "w">('l');

	const handleLengthChange = (e: any) => {
		let draftCopy: IRoofAddress = JSON.parse(JSON.stringify(selectedDraft));
		if (!draftCopy.roofs?.[0]) return;

		const {value} = e.target;
		if(value?.length === 0) draftCopy.roofs[0].length = 0;
		else if(isPositiveInteger(value)) draftCopy.roofs[0].length = value;

		if(draftCopy.roofs[0].width! > 0) {
			handleFlashing({area: true});
			draftCopy.roofs[0].area = parseInt(value) * draftCopy.roofs[0].width!
		}
		
		updateDraft(draftCopy);
		setLastChanged('l');
	};

	const handleWidthChange = (e: any) => {
		let draftCopy: IRoofAddress = JSON.parse(JSON.stringify(selectedDraft));
		if (!draftCopy.roofs?.[0]) return;
		
		const {value} = e.target;
		if(value?.length === 0) draftCopy.roofs[0].width = 0;
		else if(isPositiveInteger(value)) draftCopy.roofs[0].width = value;

		if(draftCopy.roofs[0]?.length! > 0) {
			handleFlashing({area: true});
			draftCopy.roofs[0].area = parseInt(value) * draftCopy.roofs[0]?.length!
		}

		updateDraft(draftCopy);
		setLastChanged('w');
	};

	const handleAreaChange = (e: any) => {
		let draftCopy: IRoofAddress = JSON.parse(JSON.stringify(selectedDraft));
		if (!draftCopy.roofs?.[0]) return;

		const {value} = e.target;
		if(value?.length === 0) draftCopy.roofs[0].area = 0;
		if (isPositiveInteger(value)) {

			draftCopy.roofs[0].area = value;

			if (draftCopy.roofs[0]?.length! > 0 && draftCopy.roofs[0].width! > 0) {
				if (lastChanged === 'l') {
					handleFlashing({width: true});
					draftCopy.roofs[0].width = Math.ceil(value / draftCopy.roofs[0]?.length!);
				} else {
					handleFlashing({length: true});
					draftCopy.roofs[0].length = Math.ceil(value / draftCopy.roofs[0].width!);
				}
			}
		}

		updateDraft(draftCopy);
	};

	const handleSlopeChange = (e: any) => {
		let draftCopy: IRoofAddress = JSON.parse(JSON.stringify(selectedDraft));
		if (!draftCopy.roofs?.[0]) return;

		const {value} = e.target;
		if(value?.length === 0) draftCopy.roofs[0].slope = 0;
		else if(isPositiveInteger(value)) draftCopy.roofs[0].slope = value;

		if(slopeRef.current)
			slopeRef.current.style.background = getSliderBackground(value, minSlope, maxSlope);

		updateDraft(draftCopy);
	};

	const getSliderBackground = (value: number, min: number, max: number) => {
		const percentage = ((value - min) / (max - min)) * 100;
		return `linear-gradient(to right, var(--half-white) 0%, var(--half-white) ${percentage}%, transparent ${percentage}%, transparent 100%)`;
	};

	const handleOrientationChange = (newOri: { label: string, value: number }) => {
		let draftCopy: IRoofAddress = JSON.parse(JSON.stringify(selectedDraft));
		if (!draftCopy.roofs?.[0]) return;

		draftCopy.roofs[0].orientation = newOri.value;

		updateDraft(draftCopy);
	}

	const validateInput = () => {
		let draftCopy: IRoofAddress = JSON.parse(JSON.stringify(selectedDraft));
		if (!draftCopy.roofs?.[0]) return;

		let isValidLength = isPositiveInteger(`${draftCopy.roofs[0]?.length}`);
		let isValidWidth = isPositiveInteger(`${draftCopy.roofs[0].width}`);
		let isValidArea = isPositiveInteger(`${draftCopy.roofs[0].area}`);

		let finalLength = Math.min(Math.max(draftCopy.roofs[0]?.length!, minLengthWidth), maxLengthWidth);
		let finalWidth = Math.min(Math.max(draftCopy.roofs[0].width!, minLengthWidth), maxLengthWidth);
		let finalArea = Math.min(Math.max(draftCopy.roofs[0].area, minArea), maxArea);

		handleError({
			length: isValidLength ? finalLength !== Number(draftCopy.roofs[0]?.length!) : errorFields.length,
			width: isValidWidth ? finalWidth !== Number(draftCopy.roofs[0].width!) : errorFields.width,
			area: isValidArea ? finalArea !== Number(draftCopy.roofs[0].area) : errorFields.area,
		});

		// Validate all fields on every blur, because they are all connected
		if(isValidLength) draftCopy.roofs[0].length = finalLength;
		if(isValidWidth) draftCopy.roofs[0].width = finalWidth;
		if(isValidArea) draftCopy.roofs[0].area = finalArea;

		updateDraft(draftCopy);
	}

	useEffect(() => {
		if(slopeRef.current && selectedDraft.roofs?.[0]) {
			slopeRef.current.style.background = getSliderBackground(selectedDraft.roofs[0].slope, minSlope, maxSlope);
		}
	}, [selectedDraft.roofs?.[0]?.slope]);

	const currentRoof = selectedDraft.roofs?.[0];

	return (
		<div className={`custom-roof-holder`}>
			<FloatingInput
				value={currentRoof?.length === 0 ? '' : `${currentRoof?.length || ''}`}
				onChange={handleLengthChange}
				onBlur={validateInput}
				unit={i18nMap.unit} tabIndex={tabIndex}
				label={i18nMap.length} position={{left: '0%', top: '25%'}}
				hasError={errorFields.length}
				isFlashing={flashingFields.length}
				inputMode="numeric"
			/>

			<FloatingInput
				value={currentRoof?.width === 0 ? '' : `${currentRoof?.width || ''}`}
				onChange={handleWidthChange}
				onBlur={validateInput}
				unit={i18nMap.unit} tabIndex={tabIndex}
				label={i18nMap.width} position={{left: '85%', top: '35%'}}
				hasError={errorFields.width}
				isFlashing={flashingFields.width}
				inputMode="numeric"
			/>


			<div className={`slope-slider-holder`} style={{left: '0%', top: '80%'}}>
				<div className={`slope-slider-label`}>{i18nMap.slope}</div>

				<input ref={slopeRef} className={`slope-slider`} type="range" min={minSlope} max={maxSlope}
					   value={currentRoof?.slope || 0} onChange={handleSlopeChange}/>

				<div className="slider-value">
					{currentRoof?.slope || 0} <sup>o</sup>
				</div>
			</div>

			<FloatingInput
				value={currentRoof?.area === 0 ? '' : `${currentRoof?.area || ''}`}
				onChange={handleAreaChange}
				onBlur={validateInput}
				unit={<>{i18nMap.unit}<sup>2</sup></>} tabIndex={tabIndex}
				label={i18nMap.area} position={{left: '80%', top: '80%'}}
				hasError={errorFields.area}
				isFlashing={flashingFields.area}
				inputMode="numeric"
			/>

			<OrientationPicker i18nMap={i18nMap}
							   value={directionDegrees.find(ori => ori.value === currentRoof?.orientation) || defaultOrientation}
							   onChange={handleOrientationChange} label={i18nMap.orientation}/>
		</div>
	);
}